import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { UtilityFunctions } from '../../utils/UtilityFunctions';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { bindActionCreators } from "redux";
import { actionCreators, State } from "../../store";
import { cookieService, userService } from '../../_services';
import { STRING_CONSTANTS } from '../../utils/constants/stringConstants';
import SuccessToast from '../../components/successToast';
import { checkData } from "../../utils/checkData";
import { api } from "../../middleware/api";
import { PositionOptions } from 'mapbox-gl';

// Styles object - keeping consistent with ARAffiliates
const styles = {
    container: {
        backgroundColor: '#1a1a2e',
        minHeight: '100vh',
    },
    topBar: {
        background: 'rgba(255, 255, 255, 0.05)',
        padding: '16px 24px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px',
    },
    topBarTitle: {
        color: '#ffffff',
        margin: 0,
        fontSize: '24px',
        fontWeight: '500',
    },
    blueBox: {
        background: 'rgba(255, 255, 255, 0.05)',
        borderRadius: '8px',
        padding: '20px',
        marginBottom: '20px',
    },
    whiteText: {
        color: '#ffffff',
    },
    mutedText: {
        color: 'rgba(255, 255, 255, 0.6)',
    },
    formGroup: {
        marginBottom: '24px',
    },
    formLabel: {
        color: '#ffffff',
        fontSize: '16px',
        marginBottom: '8px',
        display: 'block',
    },
    formInput: {
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        borderRadius: '4px',
        padding: '12px 16px',
        color: '#ffffff',
        width: '100%',
        maxWidth: '400px',
        fontSize: '16px',
    },
    formError: {
        color: '#ff6b6b',
        fontSize: '14px',
        marginTop: '5px',
    },
    formHint: {
        color: 'rgba(255, 255, 255, 0.6)',
        fontSize: '14px',
        marginTop: '5px',
    },
    button: {
        padding: '10px 20px',
        borderRadius: '4px',
        border: 'none',
        cursor: 'pointer',
        fontSize: '16px',
        fontWeight: '600',
        transition: 'all 0.3s ease',
    },
    primaryButton: {
        backgroundColor: '#06EFC5',
        color: '#1a1a2e',
    },
    secondaryButton: {
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        color: '#ffffff',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        marginRight: '10px',
    },
    dangerButton: {
        backgroundColor: 'rgba(255, 99, 71, 0.2)',
        color: '#ff6347',
        border: '1px solid rgba(255, 99, 71, 0.3)',
    },
    backButton: {
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        color: '#ffffff',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
    },
    optionCard: {
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: '8px',
        padding: '20px',
        marginBottom: '15px',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
    },
    selectedOptionCard: {
        border: '1px solid #06EFC5',
        backgroundColor: 'rgba(6, 239, 197, 0.1)',
    },
    optionTitle: {
        color: '#ffffff',
        fontSize: '18px',
        fontWeight: '600',
        marginBottom: '5px',
    },
    optionDescription: {
        color: 'rgba(255, 255, 255, 0.8)',
        fontSize: '14px',
    },
    splitDetails: {
        display: 'flex',
        marginTop: '15px',
        gap: '20px',
    },
    splitItem: {
        flex: 1,
        textAlign: 'center',
    },
    splitValue: {
        fontSize: '24px',
        fontWeight: '700',
        color: '#06EFC5',
        marginBottom: '5px',
    },
    splitLabel: {
        color: 'rgba(255, 255, 255, 0.6)',
        fontSize: '14px',
    },
    modalOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
    },
    modalContent: {
        backgroundColor: '#1a1a2e',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: '8px',
        padding: '30px',
        maxWidth: '500px',
        width: '90%',
    },
    modalTitle: {
        color: '#ffffff',
        fontSize: '20px',
        marginBottom: '20px',
        textAlign: 'center',
    },
    modalText: {
        color: 'rgba(255, 255, 255, 0.8)',
        fontSize: '16px',
        marginBottom: '25px',
        textAlign: 'center',
    },
    modalButtons: {
        display: 'flex',
        justifyContent: 'center',
        gap: '15px',
    },
    saveButton: {
        backgroundColor: '#06EFC5',
        color: '#1a1a2e',
        padding: '10px 20px',
        borderRadius: '4px',
        border: 'none',
        fontWeight: '600',
        cursor: 'pointer',
        fontSize: '16px',
        transition: 'all 0.3s ease',
        marginTop: '20px',
    },
    infoBox: {
        backgroundColor: 'rgba(0, 123, 255, 0.1)',
        border: '1px solid rgba(0, 123, 255, 0.3)',
        borderRadius: '8px',
        padding: '20px',
        marginBottom: '20px',
    },
    infoTitle: {
        color: '#ffffff',
        fontSize: '18px',
        marginBottom: '15px',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
    },
    infoText: {
        color: 'rgba(255, 255, 255, 0.8)',
    },
};

// Commission split options
const COMMISSION_OPTIONS = [
    {
        id: 'full_commission',
        title: '100% Commission',
        description: 'Take the full $997.50 commission. Your referrals pay the standard price.',
        commission: 997.50,
        discountAmount: 0,
        finalPrice: 1995,
        optionCode: null,
    },
    {
        id: 'commission_80',
        title: '80/20 split',
        description: 'You get $598.50 commission. Your referrals get a $399 discount on their subscription.',
        commission: 598.50,
        discountAmount: 399.00,
        finalPrice: 1596.00,
        optionCode: null,
    },
    {
        id: 'split_50_50',
        title: '50/50 split',
        description: 'You get $498.75 commission. Your referrals get a $498.75 discount on their subscription.',
        commission: 498.75,
        discountAmount: 498.75,
        finalPrice: 1496.25,
        optionCode: null,
    },
    {
        id: 'no_commission',
        title: 'No Commission, 20% Discount',
        description: 'You get no commission. Your referrals get a a $399 discount on their subscription.',
        commission: 0,
        discountAmount: 399.00,
        finalPrice: 1596.00,
        optionCode: null,
    },
];


const ARAffiliateSetting = (props) => {
    const dispatch = useDispatch();
    const state = useSelector((state) => state);
    const [userData, setUserData] = useState(null);
    const { setUnreadNotificationCount } = bindActionCreators(actionCreators, dispatch);
    const user = cookieService?.getCookie(STRING_CONSTANTS?.cookieStrings?.user);
    const isUserLoggedIn = cookieService.getCookie(STRING_CONSTANTS.cookieStrings.token);
    
    // Form state
    const [referralCode, setReferralCode] = useState('');
    const [originalCode, setOriginalCode] = useState('');
    const [codeError, setCodeError] = useState('');
    const [selectedCommissionOption, setSelectedCommissionOption] = useState('full_commission');
    const [isLoading, setIsLoading] = useState(true);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);
    const [payoutMethod, setPayoutMethod] = useState('paypal');
    const [paypalEmail, setPaypalEmail] = useState('');
    const [paypalEmailError, setPaypalEmailError] = useState('');
    const [payId, setPayId] = useState('');
    const [payIdError, setPayIdError] = useState('');
    const [registeredForGST, setRegisteredForGST] = useState(false);

    const { resetState, closeOnlineOfflineSocket } = bindActionCreators(
      actionCreators,
      dispatch
    );
    
    const handleLogout = (e) => {
      e.preventDefault();
      closeOnlineOfflineSocket(true);
      userService
        .logout()
        .then((res) => {
          api
            .deleteApi("device-token/", {
              deviceType: "WEB",
              token: `${cookieService.getCookie("fcm")}`,
            })
            .then((res) => {
              cookieService.removeAllCookies();
              props.history.push(STRING_CONSTANTS.navigationString.login);
              resetState();
            })
            .catch((e) => {
              cookieService.removeAllCookies();
              props.history.push(STRING_CONSTANTS.navigationString.login);
              resetState();
            });
        })
        .catch((err) => {
          checkData(props, "/", err);
        });
    };

    // Fetch user data and settings from API
    const fetchUserSettings = async () => {
        setIsLoading(true);
        try {
            // Get user data first
            const userData = await userService.getUserData();
            cookieService.saveCookie(STRING_CONSTANTS.cookieStrings.user, userData);
            setUnreadNotificationCount(userData?.user?.unreadNotificationCount);
            setUserData(userData);

            
            // Get commission settings
            // Get referrals data
            const affiliateResponse = await userService.aiRequest({
                method: 'get-affiliate-data',
                data: {  }  // Limit to 50 most recent interactions
            });
            if (affiliateResponse && affiliateResponse.data) {
                const data = affiliateResponse.data;

                setPayId(affiliateResponse.data.settings?.payidId || '');
                setPaypalEmail(affiliateResponse.data.settings?.paypalId || '');
                setPayoutMethod(affiliateResponse.data.settings?.paymentMode || 'paypal');
                setRegisteredForGST(affiliateResponse.data.settings?.isGstRegistered || false);
                setReferralCode(data.code);
                setOriginalCode(data.code);
                console.log("Setting commission option to:", affiliateResponse.data.settings?.commissionSplit);
                setSelectedCommissionOption(affiliateResponse.data.settings?.commissionSplit || 'full_commission');

                // Loop through COMMISSION_OPTIONS and set the optionCode for each
                COMMISSION_OPTIONS.forEach(option => {
                    option.optionCode = data.code;
                    if (option.id === 'full_commission') {
                        option.optionCode = data.code + '';
                    } else if (option.id === 'commission_80') {
                        option.optionCode = data.code + 'A';
                    }
                    else if (option.id === 'split_50_50') {
                        option.optionCode = data.code + 'B';
                    }
                    else if (option.id === 'full_discount') {
                        option.optionCode = data.code + 'C';
                    }
                    else if (option.id === 'no_commission') {
                        option.optionCode = data.code + 'D';
                    }
                });

                // Find the matching commission option
                const matchingOption = COMMISSION_OPTIONS.find(
                    option => option.id === affiliateResponse.data.settings?.commissionSplit
                );
                
                if (matchingOption) {
                    setSelectedCommissionOption(matchingOption.id);
                }
                if (affiliateResponse.data.payoutMethod) {
                    setPayoutMethod(affiliateResponse.data.payoutMethod);
                    
                    if (affiliateResponse.data.payoutMethod === 'paypal' && affiliateResponse.data.paypalId) {
                        setPaypalEmail(affiliateResponse.data.paypalId);
                    } else if (affiliateResponse.data.payoutMethod === 'payid' && affiliateResponse.data.payId) {
                        setPayId(affiliateResponse.data.payId);
                    }
                }
            }
        } catch (error) {
            console.error("Error fetching user settings:", error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (isUserLoggedIn) {
            fetchUserSettings();
        }
    }, []);
    
    // Check for changes to enable/disable save button
    useEffect(() => {
        const codeChanged = referralCode !== originalCode;
        const optionChanged = selectedCommissionOption !== 'full_commission'; // Assuming default is full commission
        setHasChanges(codeChanged || optionChanged);
    }, [referralCode, selectedCommissionOption, originalCode]);

    const handleReferralCodeChange = (e) => {
        const newCode = e.target.value.trim();
        setReferralCode(newCode);
        
        // Validate code
        if (newCode.length < 4 && newCode.length > 0) {
            setCodeError('Code must be at least 4 characters');
        } else if (newCode.length > 10) {
            setCodeError('Code cannot exceed 10 characters');
        } else {
            setCodeError('');
        }
    };

    const handleCommissionOptionSelect = (optionId) => {
        setSelectedCommissionOption(optionId);
    };

    const handleSaveChanges = () => {
        // Validate before saving
        if (referralCode.length < 4 || referralCode.length > 10) {
            setCodeError('Code must be between 4-10 characters');
            return;
        }
        
        // If code changed, show confirmation modal
        if (referralCode !== originalCode) {
            setShowConfirmModal(true);
            return;
        }
        
        // Otherwise just save changes
        saveSettings();
    };
    
    const saveSettings = async () => {
        setIsLoading(true);
        try {
            // Prepare data for API
            const settingsData = {
                referralCode: referralCode,
                payoutMethod: payoutMethod,
                paypalEmail: paypalEmail,
                payidEmail: payId,
                isGstRegistered: registeredForGST,
                commissionSplit: selectedCommissionOption,
            };
            
            // Call API to save settings using save-affiliate-data endpoint
            const response = await userService.aiRequest({
                method: 'save-affiliate-data',
                data: settingsData,
            });
            console.log("Settings saved successfully:", response);
            
            // Update original code to reflect saved state
            setOriginalCode(referralCode);
            
            SuccessToast("Success", "Your affiliate settings have been updated!");
            setShowConfirmModal(false);
        } catch (error) {
            console.error("Error saving settings:", error);
            SuccessToast("Error", "Failed to save settings. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleGoBack = () => {
        props.history.push('/ar/affiliate/dashboard');
    };

    return (
        <section className="main-dashboard-wrap" style={styles.container}>
            <Helmet>
                <title>MPLOI: Affiliate Settings</title>
            </Helmet>
            <div id="wrapper" style={{ height: "100vh" }}>
                {/* Sidebar - same as ARAffiliates */}
                <ul className={`navbar-nav bg-gradient-primary sidebar sidebar-dark accordion ${state.common.showSidebarOnMobileView ? "active" : ""}`} id="accordionSidebar">
                    <div>
                        <Link style={{ pointerEvents: "none" }} className="sidebar-brand d-flex align-items-center justify-content-center" to="#">
                            <img src={UtilityFunctions.getImageURL() + "db-logo.svg"} alt="Logo" />
                        </Link>
                        {!user?.user?.isAccountsSignup && <li className="nav-item">
                            <Link className="nav-link" to="/ar/affiliate/dashboard">
                                <img src={UtilityFunctions.getImageURL() + "home-icon-blank-white.svg"} alt="" />
                                <img src={UtilityFunctions.getImageURL() + "home-icon-fill-green.svg"} alt="" />
                            </Link>
                        </li>}
                    </div>

                    <div>
                        <li className='d-grid justify-content-center gap-2 text-white'>
                            <span style={{ cursor: "pointer" }}
                                onClick={handleLogout}
                            >
                                Logout
                            </span>
                        </li>
                    </div>
                </ul>

                {/* Content Wrapper */}
                <div id="content-wrapper" className="d-flex flex-column">
                    <div id="content">
                        {/* Top Bar with Back Button */}
                        <div style={styles.topBar}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                <button 
                                    style={{...styles.button, ...styles.backButton}}
                                    onClick={handleGoBack}
                                >
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M15 8H1M1 8L8 15M1 8L8 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                    Back
                                </button>
                                <h1 style={styles.topBarTitle}>Affiliate Settings</h1>
                            </div>
                        </div>

                        {/* Main Content */}
                        <div className="container-fluid">
                            {isLoading ? (
                                <div style={styles.blueBox}>
                                    <p style={styles.whiteText}>Loading your settings...</p>
                                </div>
                            ) : (
                                <>
                                    {/* Referral Code Section */}
                                    <div style={styles.blueBox}>
                                        <h3 style={{...styles.whiteText, marginBottom: '20px'}}>
                                            Your Referral Code
                                        </h3>
                                        
                                        <div style={styles.formGroup}>
                                            <input 
                                                type="text"
                                                style={styles.formInput}
                                                value={referralCode}
                                                onChange={handleReferralCodeChange}
                                                placeholder="Enter your custom code"
                                            />
                                            {codeError && (
                                                <p style={styles.formError}>{codeError}</p>
                                            )}
                                            <p style={styles.formHint}>
                                                Your referral code must be between 4-10 characters. 
                                                This will appear in your affiliate URL: https://mploi.com/r/{referralCode || '[code]'}
                                            </p>
                                        </div>
                                        
                                        <div style={styles.infoBox}>
                                            <h4 style={styles.infoTitle}>Important Note</h4>
                                            <p style={styles.infoText}>
                                                Changing your referral code will create a new affiliate link. Your old link will no longer work, 
                                                and you'll need to update any marketing materials where you've shared your previous link.
                                            </p>
                                        </div>

                                        <div style={{ display: 'flex', justifyContent: 'left', marginTop: '20px' }}>
                                            <button 
                                                style={{...styles.button, ...styles.primaryButton}}
                                                onClick={handleSaveChanges}
                                            >
                                                Save Changes
                                            </button>
                                        </div>
                                    </div>

                                    {/* Payout Method Section */}
                                    <div style={styles.blueBox}>
                                        <h3 style={{...styles.whiteText, marginBottom: '20px'}}>
                                            Payout Method
                                        </h3>
                                        
                                        <p style={styles.mutedText}>
                                            Choose how you'd like to receive your affiliate commission payments.
                                        </p>
                                        
                                        <div style={{ marginTop: '20px' }}>
                                            <div style={styles.formGroup}>
                                                <label style={styles.formLabel}>
                                                    Payout Method
                                                </label>
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', marginTop: '10px' }}>
                                                    <div 
                                                        style={{
                                                            ...styles.optionCard,
                                                            ...(payoutMethod === 'paypal' ? styles.selectedOptionCard : {}),
                                                            padding: '15px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '15px'
                                                        }}
                                                        onClick={() => setPayoutMethod('paypal')}
                                                    >
                                                        <div style={{ 
                                                            width: '24px', 
                                                            height: '24px', 
                                                            borderRadius: '50%', 
                                                            border: '2px solid ' + (payoutMethod === 'paypal' ? '#06EFC5' : 'rgba(255, 255, 255, 0.3)'),
                                                            display: 'flex',
                                                            justifyContent: 'center',
                                                            alignItems: 'center'
                                                        }}>
                                                            {payoutMethod === 'paypal' && (
                                                                <div style={{ 
                                                                    width: '12px', 
                                                                    height: '12px', 
                                                                    borderRadius: '50%', 
                                                                    backgroundColor: '#06EFC5' 
                                                                }}></div>
                                                            )}
                                                        </div>
                                                        <div>
                                                            <h4 style={styles.optionTitle}>PayPal</h4>
                                                            <p style={styles.optionDescription}>Receive payments to your PayPal account</p>
                                                        </div>
                                                    </div>
                                                    
                                                    <div 
                                                        style={{
                                                            ...styles.optionCard,
                                                            ...(payoutMethod === 'payid' ? styles.selectedOptionCard : {}),
                                                            padding: '15px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '15px'
                                                        }}
                                                        onClick={() => setPayoutMethod('payid')}
                                                    >
                                                        <div style={{ 
                                                            width: '24px', 
                                                            height: '24px', 
                                                            borderRadius: '50%', 
                                                            border: '2px solid ' + (payoutMethod === 'payid' ? '#06EFC5' : 'rgba(255, 255, 255, 0.3)'),
                                                            display: 'flex',
                                                            justifyContent: 'center',
                                                            alignItems: 'center'
                                                        }}>
                                                            {payoutMethod === 'payid' && (
                                                                <div style={{ 
                                                                    width: '12px', 
                                                                    height: '12px', 
                                                                    borderRadius: '50%', 
                                                                    backgroundColor: '#06EFC5' 
                                                                }}></div>
                                                            )}
                                                        </div>
                                                        <div>
                                                            <h4 style={styles.optionTitle}>PayID</h4>
                                                            <p style={styles.optionDescription}>Receive payments using your email or phone PayID</p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            {payoutMethod === 'paypal' && (
                                                <div style={{...styles.formGroup, marginTop: '20px'}}>
                                                    <label style={styles.formLabel}>
                                                        PayPal Email Address
                                                    </label>
                                                    <input 
                                                        type="email"
                                                        style={styles.formInput}
                                                        value={paypalEmail}
                                                        onChange={(e) => setPaypalEmail(e.target.value)}
                                                        placeholder="your-email@example.com"
                                                    />
                                                    {paypalEmailError && (
                                                        <p style={styles.formError}>{paypalEmailError}</p>
                                                    )}
                                                </div>
                                            )}
                                            
                                            {payoutMethod === 'payid' && (
                                                <div style={{...styles.formGroup, marginTop: '20px'}}>
                                                    <label style={styles.formLabel}>
                                                        PayID (Email or Phone)
                                                    </label>
                                                    <input 
                                                        type="text"
                                                        style={styles.formInput}
                                                        value={payId}
                                                        onChange={(e) => setPayId(e.target.value)}
                                                        placeholder="email@example.com or +61412345678"
                                                    />
                                                    {payIdError && (
                                                        <p style={styles.formError}>{payIdError}</p>
                                                    )}
                                                    <p style={styles.formHint}>
                                                        Enter either your email address or phone number associated with your PayID
                                                    </p>
                                                </div>
                                            )}
                                            {/* Add an is GST registered boolean switch */}
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '20px' }}>
                                                <input 
                                                    type="checkbox" 
                                                    id="is-gst-registered" 
                                                    name="is-gst-registered" 
                                                    style={{ width: '20px', height: '20px' }} 
                                                    checked={registeredForGST}
                                                    onChange={(e) => setRegisteredForGST(e.target.checked)}
                                                />
                                                <label htmlFor="is-gst-registered" style={styles.mutedText}>
                                                    I am registered for GST
                                                </label>
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'left', marginTop: '20px' }}>
                                            <button 
                                                style={{...styles.button, ...styles.primaryButton}}
                                                onClick={handleSaveChanges}
                                            >
                                                Save Changes
                                            </button>
                                        </div>
                                    </div>

                                    {/* Commission Split Section */}
                                    <div style={styles.blueBox}>
                                        <h3 style={{...styles.whiteText, marginBottom: '20px'}}>
                                            Commission vs. Discount Split
                                        </h3>
                                        
                                        <p style={styles.mutedText}>
                                            Choose how you want to split the $997.50 commission between yourself and your referrals. The option you select is the default for your referral code {referralCode}, but you can use any of the codes on a one-time basis.
                                        </p>
                                        
                                        <div style={{ marginTop: '20px' }}>
                                            {COMMISSION_OPTIONS.map((option) => (
                                                <div 
                                                    key={option.id}
                                                    style={{
                                                        ...styles.optionCard,
                                                        ...(selectedCommissionOption === option.id ? styles.selectedOptionCard : {})
                                                    }}
                                                    onClick={() => handleCommissionOptionSelect(option.id)}
                                                >
                                                    <h4 style={styles.optionTitle}>{option.title}</h4>
                                                    <p style={styles.optionDescription}>{option.description}</p>
                                                    
                                                    <div style={styles.splitDetails}>
                                                        <div style={styles.splitItem}>
                                                            <div style={styles.splitValue}>${option.commission}</div>
                                                            <div style={styles.splitLabel}>Your Commission</div>
                                                        </div>
                                                        <div style={styles.splitItem}>
                                                            <div style={styles.splitValue}>${option.discountAmount}</div>
                                                            <div style={styles.splitLabel}>Customer Discount</div>
                                                        </div>
                                                        <div style={styles.splitItem}>
                                                            <div style={styles.splitValue}>${option.finalPrice} + GST</div>
                                                            <div style={styles.splitLabel}>Customer Pays</div>
                                                        </div>
                                                        <div style={styles.splitItem}>
                                                            <div style={styles.splitValue}>{option.optionCode}</div>
                                                            <div style={styles.splitLabel}>Option code</div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'left', marginTop: '20px' }}>
                                            <button 
                                                style={{...styles.button, ...styles.primaryButton}}
                                                onClick={handleSaveChanges}
                                            >
                                                Save Changes
                                            </button>
                                        </div>
                                    </div>

                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Confirmation Modal */}
            {showConfirmModal && (
                <div style={styles.modalOverlay}>
                    <div style={styles.modalContent}>
                        <h3 style={styles.modalTitle}>Change Referral Code?</h3>
                        <p style={styles.modalText}>
                            Are you sure you want to change your referral code from <strong>{originalCode}</strong> to <strong>{referralCode}</strong>?
                            <br /><br />
                            Your old affiliate link will no longer work, and you'll need to update any marketing materials where you've shared your previous link.
                        </p>
                        <div style={styles.modalButtons}>
                            <button 
                                style={{...styles.button, ...styles.secondaryButton}}
                                onClick={() => setShowConfirmModal(false)}
                            >
                                Cancel
                            </button>
                            <button 
                                style={{...styles.button, ...styles.primaryButton}}
                                onClick={saveSettings}
                            >
                                Yes, Change Code
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
};

export default ARAffiliateSetting;